import React from 'react';
import { Container, Box, Typography, Link, Paper } from '@mui/material';
import Footer from './Footer';

const COMPANY = 'Unidatum Integrated Products Limited';
const ADDRESS_LINES = [
  'Venture Hub',
  '136 Capel Street',
  'Dublin, D01 T2C9, IRELAND',
];
const PHONE = '+353 1 265 7898';
const CONTACT_EMAIL = 'privacy@alignresume.com';
const SITE = 'alignresume.com';
const EFFECTIVE = 'June 2026';

const H = (props: { children: React.ReactNode; id?: string }) => (
  <Typography
    id={props.id}
    variant="h6"
    sx={{ mt: 3, mb: 1, color: 'primary.main', scrollMarginTop: '80px' }}
  >
    {props.children}
  </Typography>
);

const P = (props: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
    {props.children}
  </Typography>
);

const LegalShell: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Container maxWidth="md">
    <Box sx={{ my: 4 }}>
      <Link href="/" color="primary" underline="hover" variant="body2">
        ← Back to Align Resume
      </Link>
      <Typography variant="h3" component="h1" className="matrix-glow" sx={{ mt: 2, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Effective {EFFECTIVE} · {COMPANY}
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 4 } }}>{children}</Paper>
    </Box>
    <Footer />
  </Container>
);

const CompanyBlock = () => (
  <Box sx={{ mt: 1 }}>
    <Typography variant="body2">{COMPANY}</Typography>
    {ADDRESS_LINES.map((l) => (
      <Typography key={l} variant="body2" color="text.secondary">
        {l}
      </Typography>
    ))}
    <Typography variant="body2" color="text.secondary">
      Tel: {PHONE}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Email:{' '}
      <Link href={`mailto:${CONTACT_EMAIL}`} color="primary">
        {CONTACT_EMAIL}
      </Link>
    </Typography>
  </Box>
);

export const TermsPage: React.FC = () => (
  <LegalShell title="Terms of Use">
    <P>
      These Terms of Use ("Terms") govern your access to and use of {SITE} and the Align Resume
      service (the "Service"), operated by {COMPANY} ("we", "us", "our"). By accessing or using the
      Service you agree to these Terms. If you do not agree, do not use the Service.
    </P>

    <H>1. The Service</H>
    <P>
      Align Resume uses artificial intelligence to tailor a resume you provide to a job posting you
      provide. Output is generated automatically and may contain errors, omissions, or inaccuracies.
      The Service does not provide career, legal, financial, or employment advice. You are solely
      responsible for reviewing, verifying, and editing any output before relying on or submitting it.
    </P>

    <H>2. Eligibility</H>
    <P>
      You must be at least 18 years old (or the age of majority in your jurisdiction) to use the
      Service. By using the Service you represent that you meet this requirement.
    </P>

    <H>3. Free trial, credits & payment</H>
    <P>
      We offer a limited number of free optimizations. Beyond the free allowance you may purchase
      prepaid balance ("credits") to continue. Payments are processed by Stripe; we do not store your
      full card details. Credits are consumed as you use paid features and, except where a refund is
      required by applicable consumer law, purchased credits are non-refundable. Prices are shown
      before purchase and may change prospectively.
    </P>

    <H>4. Acceptable use</H>
    <P>
      You agree not to: (a) submit unlawful, infringing, or another person's content without
      authorization; (b) use bots, scrapers, or automated means to access the Service or to abuse the
      free tier; (c) attempt to circumvent usage limits, security, or rate limiting; (d) interfere
      with or disrupt the Service; or (e) use the Service to violate any law. We may rate-limit,
      suspend, or block access (including by IP address) to protect the Service.
    </P>

    <H>5. Your content & intellectual property</H>
    <P>
      You retain all rights to the resume and other content you submit ("Your Content"). You grant us
      a limited licence to process Your Content solely to provide the Service (including transmitting
      it to our AI processing provider). As between you and us, you own the optimized output generated
      for you. All software, design, and trademarks of the Service remain our property.
    </P>

    <H>6. Disclaimers</H>
    <P>
      THE SERVICE AND ALL OUTPUT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
      KIND, EXPRESS OR IMPLIED, INCLUDING FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND
      NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free, or that
      output will result in any job, interview, or outcome.
    </P>

    <H>7. Limitation of liability</H>
    <P>
      To the fullest extent permitted by law, {COMPANY} will not be liable for any indirect,
      incidental, special, consequential, or punitive damages, or for lost profits or data. Our total
      aggregate liability arising out of or relating to the Service will not exceed the greater of the
      amount you paid us in the three (3) months before the claim or EUR 100. Nothing in these Terms
      limits liability that cannot be limited under applicable law, including your mandatory statutory
      consumer rights.
    </P>

    <H>8. Indemnification</H>
    <P>
      You agree to indemnify and hold harmless {COMPANY} from any claims arising out of Your Content
      or your breach of these Terms, to the extent permitted by law.
    </P>

    <H>9. Termination</H>
    <P>
      You may stop using the Service at any time. We may suspend or terminate access if you breach
      these Terms or to protect the Service. Sections that by their nature should survive termination
      will survive.
    </P>

    <H>10. Governing law</H>
    <P>
      These Terms are governed by the laws of Ireland, and the courts of Ireland have exclusive
      jurisdiction, without prejudice to any mandatory consumer-protection rights you have in your
      country of residence.
    </P>

    <H>11. Changes</H>
    <P>
      We may update these Terms from time to time. Material changes will be reflected by updating the
      effective date above; continued use after changes constitutes acceptance.
    </P>

    <H>12. Contact</H>
    <P>Questions about these Terms:</P>
    <CompanyBlock />
  </LegalShell>
);

export const PrivacyPage: React.FC = () => (
  <LegalShell title="Privacy Policy">
    <P>
      This Privacy Policy explains how {COMPANY} ("we", "us") collects, uses, and protects personal
      data when you use {SITE} and the Align Resume service. We are the data controller.
    </P>

    <H>Data we collect</H>
    <P>
      <b>Content you submit:</b> the resume text/file and job posting (URL or text) you provide.
      <br />
      <b>Contact details:</b> a name and email address parsed from your resume, and the email/name
      provided to Stripe if you make a payment.
      <br />
      <b>Technical & usage data:</b> your IP address, a first-party identity cookie (
      <code>ra_client_id</code>), and usage metrics such as the number of optimizations and token
      counts/cost.
      <br />
      <b>Payment data:</b> processed by Stripe. We do not receive or store your full card number.
    </P>

    <H>How we use it</H>
    <P>
      To provide the Service — including transmitting your resume and the job posting to our AI
      provider (Amazon Web Services Bedrock, running Anthropic Claude models) to generate the tailored
      result; to operate the free tier and billing; to prevent fraud and abuse (rate limiting and
      blocking); to maintain your optimization history; and to improve and secure the Service.
    </P>

    <H>Sub-processors & sharing</H>
    <P>
      We use <b>Amazon Web Services</b> (cloud hosting and Bedrock AI processing) and{' '}
      <b>Stripe</b> (payments). We do not sell your personal data and do not share it for
      cross-context behavioural advertising. We may disclose data where required by law.
    </P>

    <H>International transfers</H>
    <P>
      The Service is hosted and AI processing is performed in the United States (AWS us-east-1).
      Where personal data is transferred outside the EEA/UK, we rely on appropriate safeguards such
      as the European Commission's Standard Contractual Clauses.
    </P>

    <H>Retention</H>
    <P>
      Optimization history is retained until you delete it or request deletion. Abuse-prevention
      records (e.g. IP usage counters, blocklist entries) are retained for a limited period. Payment
      records are retained as required for tax and accounting.
    </P>

    <H>Cookies</H>
    <P>
      We use a single strictly-necessary first-party cookie (<code>ra_client_id</code>) to identify
      your session and enforce the free-tier allowance. We do not use advertising or third-party
      tracking cookies.
    </P>

    <H id="eu">EU/UK residents (GDPR)</H>
    <P>
      Our legal bases are: <b>performance of a contract</b> (providing the Service you request);{' '}
      <b>legitimate interests</b> (securing the Service and preventing abuse); and <b>consent</b>{' '}
      where required. You have the right to access, rectify, erase, restrict, or port your personal
      data, to object to processing, and to withdraw consent at any time. You may lodge a complaint
      with your local supervisory authority; our lead authority is the Irish Data Protection
      Commission (
      <Link href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" color="primary">
        dataprotection.ie
      </Link>
      ). To exercise your rights, contact us using the details below.
    </P>

    <H id="us">US residents (California / CCPA-CPRA)</H>
    <P>
      In the prior 12 months we collect the categories described above (identifiers such as email and
      IP; the content you submit; commercial/payment information; and internet/usage activity) for the
      business purposes described above. We <b>do not sell</b> your personal information and{' '}
      <b>do not share</b> it for cross-context behavioural advertising. California residents have the
      right to know, access, correct, and delete personal information, and the right not to be
      discriminated against for exercising these rights. To submit a request, contact us using the
      details below; we will verify your request before responding.
    </P>

    <H>Security</H>
    <P>
      We use encryption in transit, access controls, and other measures to protect personal data. No
      method of transmission or storage is completely secure, however, and we cannot guarantee
      absolute security.
    </P>

    <H>Children</H>
    <P>The Service is not directed to, and may not be used by, anyone under 18.</P>

    <H>Changes</H>
    <P>
      We may update this Policy; material changes will be reflected by updating the effective date
      above.
    </P>

    <H>Contact</H>
    <P>To exercise your rights or ask about this Policy:</P>
    <CompanyBlock />
  </LegalShell>
);
